import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { getProjectKeyId } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import parameters from './ui/__generated__/HorizontalNavJiraInnerQuery$parameters';
import type { HorizontalNavJiraInnerQuery } from './ui/__generated__/HorizontalNavJiraInnerQuery.graphql';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

// Route params and the rapidView query param can carry non-board segments (e.g. "filter", "null"),
// which the backend rejects as an invalid ARI. https://hello.jira.atlassian.cloud/browse/SEA-4066
const NUMERIC_BOARD_ID_PATTERN = /^\d+$/;
const LIST_NAVIGATION_ITEM_ID = 'list';
const LIST_ROUTE_SEGMENT = /\/list(\/|$)/;

const getListNavigationItemId = (match: EntryPointRouteParams['context']['match']) => {
	if (!LIST_ROUTE_SEGMENT.test(match.path ?? '')) {
		return null;
	}

	const itemIdSlug = match.params.navigationItemId;

	return itemIdSlug ? `${LIST_NAVIGATION_ITEM_ID}/${itemIdSlug}` : LIST_NAVIGATION_ITEM_ID;
};

type JiraHorizontalNavEntrypointComponent = EntryPointComponent<
	{
		horizontalNavQuery: HorizontalNavJiraInnerQuery;
	},
	{},
	{},
	{}
>;

export const jiraHorizontalNavEntrypoint: EntryPoint<
	JiraHorizontalNavEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<JiraHorizontalNavEntrypointComponent>(
		/* webpackChunkName: "async-jira-horizontal-nav-entrypoint" */ './ui/HorizontalNavJiraInner.tsx',
		{
			entryFsSsrLiftImportToModule: true,
			includeInShellOnlySsrBundle: true,
		},
	),
	getPreloadProps: ({
		context: { match, query },
		tenantContext: { cloudId },
	}: EntryPointRouteParams) => {
		let boardId = match.params.boardId;
		const { projectKey, projectId } = getProjectKeyId(match, query);
		const projectIdOrKey = projectId || projectKey;
		if (!boardId) {
			// If there is no boardId in the URL then we should check to see if it is provided as part of
			// the Connect app rapidView query param. This has been added to address JRACLOUD-94629
			const rapidViewBoard = query.rapidView ? query.rapidView : null;
			boardId = rapidViewBoard || boardId || null;
		}
		let boardAri = boardId ? `ari:cloud:jira-software:${cloudId}:board/${boardId}` : '';

		if (fg('sea-4066-sentry-error-wrong-board-ari')) {
			boardAri =
				boardId && NUMERIC_BOARD_ID_PATTERN.test(boardId)
					? JiraSoftwareBoardAri.create({ siteId: cloudId, boardId }).toString()
					: '';
		}
		const projectAri = projectId
			? JiraProjectAri.create({
					siteId: cloudId,
					projectId,
				}).toString()
			: '';
		const scopeId = boardAri || projectAri;
		const itemId = getListNavigationItemId(match);
		let containerNavigationInput;
		if (scopeId) {
			containerNavigationInput = { scopeId };
		} else if (
			projectKey &&
			itemId &&
			expVal('jracloud-99654-list-url-boardid', 'isEnabled', false)
		) {
			containerNavigationInput = { projectKeyAndItemIdQuery: { cloudId, projectKey, itemId } };
		} else {
			containerNavigationInput = { projectKeyQuery: { cloudId, projectKey } };
		}
		// mostRecentlyViewedBoard is only ever rendered as a fallback for company-managed (CMP)
		// software project routes that don't have a board in the URL (the nav header discards it for
		// TMP and whenever a board ARI is known). CMP routes carry a /software/c/ segment in the URL,
		// while TMP routes use /software/projects/; only the CMP board view loses its /c prefix during
		// URL harmonization, and that route always has a board id, so it is still skipped.
		// We only request mostRecentlyViewedBoard for boardless CMP routes.
		const isCMPRoute = match.url.includes('/software/c/');
		// The item-specific input is only built when JRACLOUD-99654-list-URL-boardId is enabled.
		const withResolvedBoard = 'projectKeyAndItemIdQuery' in containerNavigationInput;
		const skipMostRecentlyViewedBoard = !(isCMPRoute && !boardAri);
		return {
			queries: {
				horizontalNavQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters,
					variables: {
						cloudId,
						projectIdOrKey,
						withProject: Boolean(projectIdOrKey),
						boardId: boardAri,
						withBoard: Boolean(boardAri),
						containerNavigationInput,
						skipMostRecentlyViewedBoard,
						withResolvedBoard,
						first: NAVIGATION_ITEMS_PAGE_SIZE,
					},
				},
			},
		};
	},
});
